// @flow
import React, { Component } from "react";
import classNames from "classnames";
import DropdownTreeSelect from "react-dropdown-tree-select";
import isEqual from "lodash/isEqual";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import { withStyles } from "@material-ui/core/styles";

import type { DictionaryType } from "../../types/GeneralTypes";
import type { StyledTextFieldClassesType } from "./StyledTextField";
import type { IconStylesClassesType } from "../../styles/iconStyles";
import type { ButtonStylesClassesType } from "../../styles/buttonStyles";
import type { SingleTextFieldClassesType } from "./SingleTextField";
import { InputStyles } from "./StyledTextField";
import { iconStyles } from "../../styles/iconStyles";
import { buttonStyles } from "../../styles/buttonStyles";
import { baseStyles } from "./SingleTextField";

import "react-dropdown-tree-select/dist/styles.css";
import "./tree.css";

const styles = (theme: DictionaryType): DictionaryType => ({
  ...InputStyles(theme),
  ...iconStyles(theme),
  ...buttonStyles(theme),
  ...baseStyles(theme),
  root: {
    width: "100%",
    marginTop: theme.spacing.unit * 4,
    textAlign: "left"
  }
});

type TreeSelectPropsType = {
  classes: {
    ...StyledTextFieldClassesType,
    ...IconStylesClassesType,
    ...ButtonStylesClassesType,
    ...SingleTextFieldClassesType,
    root: DictionaryType
  },
  label: string,
  name: string,
  data: Array<DictionaryType>,
  onChange: Function,
  onItemClick?: Function,
  isRequired?: boolean,
  isInvalid?: boolean,
  isDisabled?: boolean
};

type TreeSelectStatesType = {
  data: Array<DictionaryType>
};

class TreeSelect extends Component<TreeSelectPropsType, TreeSelectStatesType> {
  constructor(props: TreeSelectPropsType) {
    super(props);
    this.state = { data: props.data };
  }

  static getDerivedStateFromProps(
    props: TreeSelectPropsType,
    state: TreeSelectStatesType
  ): TreeSelectStatesType {
    if (!isEqual(props.data, state.data)) {
      return {
        data: props.data
      };
    }
    return null;
  }

  shouldComponentUpdate = (nextProps: TreeSelectPropsType): boolean => {
    return (
      !isEqual(nextProps.data, this.state.data) ||
      nextProps.isInvalid !== this.props.isInvalid ||
      nextProps.isDisabled !== this.props.isDisabled
    );
  };

  getAllValues = (selectedNodes: Array<*>): Array<string> => {
    let values = [];
    selectedNodes.forEach((node: *) => {
      if (typeof node.value === "object" && node.value.constructor === Array) {
        values.push(...node.value);
      } else {
        values.push(node.value);
      }
    });

    return values;
  };

  onChange = (currentNode: *, selectedNodes: Array<*>) => {
    if (!currentNode.hasOwnProperty("children")) {
      this.props.onItemClick && this.props.onItemClick(currentNode.value);
    }

    const values = this.getAllValues(selectedNodes);
    this.props.onChange(this.props.name, values);
  };

  render(): React$Element<*> {
    const classes = this.props.classes;

    return (
      <FormControl fullWidth={true} disabled={this.props.isDisabled}>
        {this.props.label !== "" && (
          <InputLabel
            htmlFor={this.props.name}
            required={this.props.isRequired}
            error={this.props.isInvalid}
            shrink={true}
            classes={{
              root: classes.greenLabel,
              formControl: classes.textFieldFormLabel,
              focused: classes.labelFocused,
              error: classes.labelError,
              disabled: classes.labelDisabled
            }}
          >
            {this.props.label}
          </InputLabel>
        )}
        <DropdownTreeSelect
          keepTreeOnSearch
          keepChildrenOnSearch
          showPartiallySelected
          clearSearchOnChange
          disabled={this.props.isDisabled}
          data={this.state.data}
          onChange={this.onChange}
          className="mdl-demo"
        />
      </FormControl>
    );
  }
}

export default withStyles(styles)(TreeSelect);
